/*
 * Chill Country custom Reviews widget.
 * Pulls reviews from Chill Country's OWN reviews API (ccd-dev dashboard), which
 * serves our owned review data (klaviyo_reviews) — published + featured only,
 * PII-scrubbed. Accurate aggregate over the full set + true server-side
 * filter/sort/pagination (no Klaviyo client-API ~100 cap).
 *
 *   GET <api-base>/api/public/reviews?rating=&media=1&sort=recent|highest|lowest
 *       &limit=&offset=
 */
(function () {
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function normKey(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function starRow(value, cls) {
    var v = Math.max(0, Math.min(5, value));
    // Width is set after insertion by fillStars() from the MEASURED track, so the
    // fractional fill is exact regardless of font glyph width / letter-spacing.
    return (
      '<span class="ccrev-stars ' + (cls || '') + '" data-starval="' + v + '" role="img" aria-label="' +
      (Math.round(value * 10) / 10) + ' out of 5 stars">' +
      '<span class="ccrev-stars__bg">★★★★★</span>' +
      '<span class="ccrev-stars__fg">★★★★★</span></span>'
    );
  }

  // Measure each star track and fill by units, minus the trailing letter-spacing gap,
  // so e.g. 4.8 clips ~80% into the 5th star (a flat % of the spaced track over-fills).
  function fillStars(scope) {
    var els = (scope || document).querySelectorAll('.ccrev-stars[data-starval]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var bg = el.querySelector('.ccrev-stars__bg');
      var fg = el.querySelector('.ccrev-stars__fg');
      if (!bg || !fg) continue;
      var w = bg.getBoundingClientRect().width;
      if (!w) continue;
      var ls = parseFloat(getComputedStyle(el).letterSpacing);
      if (isNaN(ls)) ls = 0;
      var v = parseFloat(el.getAttribute('data-starval')) || 0;
      fg.style.width = (v / 5) * (w - ls) + 'px';
    }
  }

  function timeAgo(iso) {
    var then = Date.parse(iso);
    if (isNaN(then)) return '';
    var s = Math.max(0, (Date.now() - then) / 1000);
    var units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (var i = 0; i < units.length; i++) {
      var n = Math.floor(s / units[i][1]);
      if (n >= 1) return n + ' ' + units[i][0] + (n > 1 ? 's' : '') + ' ago';
    }
    return 'just now';
  }

  var VERIFIED_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10" fill="currentColor"/>' +
    '<path d="M8 12.5l2.5 2.5L16 9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class CCReviews {
    constructor(root) {
      this.root = root;
      this.api = (root.dataset.apiBase || 'https://dashboard.chillcountry.com').replace(/\/+$/, '') + '/api/public/reviews';
      this.perPage = parseInt(root.dataset.perPage, 10) || 8;
      this.q = function (sel) { return root.querySelector(sel); };

      // canonical "flavor|dose" (and "flavor|") → { label, url } map (server-rendered),
      // so each review footer shows a clean label + PDP link keyed off the normalized
      // flavor/dose the API returns — order-independent, unlike matching the raw title.
      this.fdmap = {};
      var fdEl = root.querySelector('[data-ccrev-fd]');
      if (fdEl) { try { this.fdmap = JSON.parse(fdEl.textContent || '{}'); } catch (e) {} }

      this.items = [];
      this.offset = 0;
      this.total = 0;
      this.hasMore = false;
      this.filterRating = 0;
      this.withMedia = false;
      this.sort = 'recent';
      this.flavorFilter = '';
      this.doseFilter = '';
      this.reqId = 0;
      this.enterFrom = null; // index from which to fade newly loaded cards in

      // initial summary paint from server-rendered metafields (avoids layout shift)
      var initAvg = parseFloat(root.dataset.initAvg);
      var initStars = this.q('[data-ccrev-avg-stars]');
      if (initStars && !isNaN(initAvg)) initStars.innerHTML = starRow(initAvg);
      var initHdrStars = this.q('[data-ccrev-hdr-stars]');
      if (initHdrStars && !isNaN(initAvg)) initHdrStars.innerHTML = starRow(initAvg);
      fillStars(root);

      this.bindControls();
      this.reload();
    }

    bindControls() {
      var self = this;
      var pills = this.q('[data-ccrev-ratingpills]');
      var toggle = this.q('[data-ccrev-filter-toggle]');
      // Rating filters live behind the funnel toggle, shown below the dropdown.
      if (toggle && pills) toggle.addEventListener('click', function () {
        var open = pills.hidden;
        pills.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (pills) pills.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-rating]');
        if (!btn) return;
        self.filterRating = parseInt(btn.getAttribute('data-rating'), 10) || 0;
        var all = pills.querySelectorAll('[data-rating]');
        for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', all[i] === btn ? 'true' : 'false');
        // keep the toggle highlighted while a specific rating is active
        if (toggle) toggle.setAttribute('data-active', self.filterRating ? 'true' : 'false');
        self.reload();
      });
      // Custom product dropdown (PDP-style): toggle, outside-click close, select.
      var dd = this.q('[data-ccrev-dd]');
      if (dd) {
        var ddBtn = dd.querySelector('[data-ccrev-dd-btn]');
        var ddMenu = dd.querySelector('[data-ccrev-dd-menu]');
        var ddTitle = dd.querySelector('[data-ccrev-dd-title]');
        var ddSub = dd.querySelector('[data-ccrev-dd-sub]');
        var ddDot = dd.querySelector('[data-ccrev-dd-dot]');
        var setOpen = function (open) {
          dd.setAttribute('data-open', open ? 'true' : 'false');
          ddBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
          ddMenu.hidden = !open;
        };
        ddBtn.addEventListener('click', function (e) { e.stopPropagation(); setOpen(ddMenu.hidden); });
        document.addEventListener('click', function (e) { if (!dd.contains(e.target)) setOpen(false); });
        ddMenu.addEventListener('click', function (e) {
          var opt = e.target.closest('.ccrev-dd__opt');
          if (!opt) return;
          var opts = ddMenu.querySelectorAll('.ccrev-dd__opt');
          for (var i = 0; i < opts.length; i++) opts[i].setAttribute('aria-selected', opts[i] === opt ? 'true' : 'false');
          var parts = (opt.getAttribute('data-val') || '').split('|');
          self.flavorFilter = parts[0] || '';
          self.doseFilter = parts[1] || '';
          ddTitle.textContent = opt.getAttribute('data-title') || 'All Flavors & Strains';
          var sub = opt.getAttribute('data-sub') || '';
          if (sub) { ddSub.textContent = sub; ddSub.hidden = false; } else { ddSub.hidden = true; }
          var color = opt.getAttribute('data-color');
          if (color) { ddDot.style.background = color; ddDot.hidden = false; } else { ddDot.hidden = true; }
          setOpen(false);
          self.reload();
        });
      }
      var more = this.q('[data-ccrev-more]');
      if (more) more.addEventListener('click', function () { self.loadMore(); });
      var list = this.q('[data-ccrev-list]');
      if (list) list.addEventListener('click', function (e) {
        var img = e.target.closest('.ccrev__item-media img');
        if (img) self.openLightbox(img.dataset.full || img.src, img.alt);
      });
    }

    queryString() {
      var qs = 'limit=' + this.perPage + '&offset=' + this.offset + '&sort=' + encodeURIComponent(this.sort);
      if (this.filterRating) qs += '&rating=' + this.filterRating;
      if (this.withMedia) qs += '&media=1';
      if (this.flavorFilter) qs += '&flavor=' + encodeURIComponent(this.flavorFilter);
      if (this.doseFilter) qs += '&dose=' + encodeURIComponent(this.doseFilter);
      return qs;
    }

    async fetchPage() {
      var res = await fetch(this.api + '?' + this.queryString(), { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error('status ' + res.status);
      var j = await res.json();
      if (!j || !j.ok) throw new Error('api error');
      return j.data;
    }

    async reload() {
      var rid = ++this.reqId;
      this.offset = 0;
      this.items = [];
      this.enterFrom = null;
      this.q('[data-ccrev-loading]').hidden = false;
      this.q('[data-ccrev-empty]').hidden = true;
      this.q('[data-ccrev-more]').hidden = true;
      try {
        var data = await this.fetchPage();
        if (rid !== this.reqId) return; // stale
        this.q('[data-ccrev-loading]').hidden = true;
        this.renderSummary(data.summary, data.total, data.average);
        this.total = data.total;
        this.hasMore = data.hasMore;
        this.items = data.reviews || [];
        this.renderList();
      } catch (e) {
        if (rid !== this.reqId) return;
        this.q('[data-ccrev-loading]').hidden = true;
        if (!this.items.length) this.q('[data-ccrev-empty]').hidden = false;
      }
    }

    async loadMore() {
      this.offset += this.perPage;
      try {
        var data = await this.fetchPage();
        this.hasMore = data.hasMore;
        this.enterFrom = this.items.length; // fade in the cards appended from here
        this.items = this.items.concat(data.reviews || []);
        this.renderList();
      } catch (e) {
        this.offset -= this.perPage; // allow retry
      }
    }

    renderSummary(summary, filteredTotal, filteredAvg) {
      if (!summary) return;
      // Filter-aware average: the API returns data.average scoped to the active filter.
      // Fall back to the global summary.average when it's missing (older API, before the
      // filtered-average field deployed).
      var avg = (filteredAvg != null && !isNaN(filteredAvg)) ? filteredAvg : (summary.average || 0);
      var count = summary.total || 0;
      // Flavor label for the "Showing X reviews for ___" line — tracks the active filter.
      var flavLabel = 'All Flavors & Strains';
      if (this.flavorFilter === 'texas-trio') flavLabel = 'The Texas Trio Bundle';
      else if (this.flavorFilter) {
        var ddTitleEl = this.q('[data-ccrev-dd-title]');
        if (ddTitleEl && ddTitleEl.textContent.trim()) flavLabel = ddTitleEl.textContent.trim();
      }
      var flavEl = this.q('[data-ccrev-flavorlabel]');
      if (flavEl) flavEl.textContent = flavLabel;
      var avgEl = this.q('[data-ccrev-avg]');
      if (avgEl) avgEl.textContent = (Math.round(avg * 100) / 100).toFixed(2);
      var numEl = this.q('[data-ccrev-count-num]');
      if (numEl) numEl.textContent = count.toLocaleString();
      var hdrStars = this.q('[data-ccrev-hdr-stars]');
      if (hdrStars) hdrStars.innerHTML = starRow(avg);
      // legacy summary nodes (guarded — removed in condensed header)
      var avgStars = this.q('[data-ccrev-avg-stars]');
      if (avgStars) avgStars.innerHTML = starRow(avg);
      fillStars(this.root);
      var countEl = this.q('[data-ccrev-count]');
      if (countEl) countEl.textContent = count.toLocaleString() + ' review' + (count === 1 ? '' : 's');
      var totalEl = this.q('[data-ccrev-total]');
      if (totalEl) totalEl.textContent = (filteredTotal != null ? filteredTotal : count).toLocaleString();
      var dist = summary.distribution || {};
      var distTotal = count || 1;
      for (var star = 1; star <= 5; star++) {
        var pct = Math.round(((dist[star] || 0) / distTotal) * 100);
        var bar = this.root.querySelector('[data-ccrev-bar="' + star + '"]');
        var lbl = this.root.querySelector('[data-ccrev-distpct="' + star + '"]');
        if (bar) bar.style.width = pct + '%';
        if (lbl) lbl.textContent = pct + '%';
      }
    }

    renderList() {
      var listEl = this.q('[data-ccrev-list]');
      var emptyEl = this.q('[data-ccrev-empty]');
      var moreEl = this.q('[data-ccrev-more]');
      if (!this.items.length) {
        listEl.innerHTML = '';
        emptyEl.hidden = false;
        moreEl.hidden = true;
        return;
      }
      emptyEl.hidden = true;
      var fdmap = this.fdmap || {};
      var enterFrom = this.enterFrom;
      listEl.innerHTML = this.items.map(function (r, i) {
        var media = (r.images && r.images.length)
          ? '<div class="ccrev__item-media">' + r.images.map(function (u) {
              return '<img src="' + esc(u) + '" data-full="' + esc(u) + '" alt="Customer photo" loading="lazy">';
            }).join('') + '</div>'
          : '';
        var verified = r.verified
          ? '<span class="ccrev__verified">' + VERIFIED_SVG + 'Verified buyer</span>'
          : '';

        // Footer: clean flavor[·dose·type] label + PDP link, looked up by the review's
        // normalized flavor/dose (order-independent). Falls back flavor+dose → flavor-only;
        // reviews with no resolved flavor get no footer (no reliable label).
        var footer = '';
        var info = fdmap[(r.flavor || '') + '|' + (r.dose || '')] || (r.flavor ? fdmap[r.flavor + '|'] : null);
        if (info && info.label) {
          var segs = String(info.label).split(' · ');
          var inner = '<span class="ccrev__item-product-flavor">' + esc(segs[0]) + '</span>';
          if (segs.length > 1) inner += ' · ' + esc(segs.slice(1).join(' · '));
          footer = info.url
            ? '<a class="ccrev__item-product" href="' + esc(info.url) + '">' + inner + '</a>'
            : '<div class="ccrev__item-product">' + inner + '</div>';
        }
        // A review covering more than one flavor (own collection, D9): list every flavor received.
        else if (r.flavors && r.flavors.length > 1) {
          var FLAVOR_NAMES = { 'ruby-red-grapefruit': 'Ruby Red Grapefruit', 'apple-cinnamon': 'Apple Cinnamon', 'agave-lime': 'Agave Lime' };
          var names = r.flavors.map(function (f) { return FLAVOR_NAMES[f] || f; });
          footer = '<div class="ccrev__item-product"><span class="ccrev__item-product-flavor">' + esc(names.join(' + ')) + '</span></div>';
        }

        var enterCls = (enterFrom != null && i >= enterFrom) ? ' ccrev__item--enter' : '';
        return (
          '<div class="ccrev__item' + enterCls + '">' +
          '<div class="ccrev__item-top">' +
          '<span class="ccrev__item-stars">' + starRow(r.rating) + '</span>' +
          '<span class="ccrev__item-date">' + esc(timeAgo(r.createdAt)) + '</span></div>' +
          '<div class="ccrev__item-author">' + esc(r.author) + verified + '</div>' +
          (r.title ? '<div class="ccrev__item-body" style="font-weight:700">' + esc(r.title) + '</div>' : '') +
          (r.content ? '<div class="ccrev__item-body">' + esc(r.content) + '</div>' : '') +
          media +
          footer +
          '</div>'
        );
      }).join('');
      fillStars(listEl);
      this.enterFrom = null;
      moreEl.hidden = !this.hasMore;
    }

    openLightbox(src, alt) {
      if (!src) return;
      var box = document.createElement('div');
      box.className = 'ccrev-lightbox';
      box.innerHTML = '<img src="' + esc(src) + '" alt="' + esc(alt) + '">';
      var close = function () { box.remove(); document.removeEventListener('keydown', onKey); };
      var onKey = function (e) { if (e.key === 'Escape') close(); };
      box.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      document.body.appendChild(box);
    }
  }

  function init() {
    document.querySelectorAll('[data-ccrev]').forEach(function (el) {
      if (!el._ccrev) el._ccrev = new CCReviews(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
